import { useEffect, useState } from 'react'
import { Play, Check, Loader2, ChevronLeft } from 'lucide-react'
import { ExerciseDetail } from '@renderer/types/api'

export type LaunchMode = 'intro' | 'provisioning'

interface LaunchCardProps {
  exercise: ExerciseDetail
  mode: LaunchMode
  onLaunch: () => void
  onBack: () => void
  onProvisioned: () => void
  onConnecting?: () => void
  launchReady?: boolean
  teachingMode: 'guide' | 'assist'
  onTeachingModeChange: (mode: 'guide' | 'assist') => void
}

const MODES: {
  value: 'guide' | 'assist'
  label: string
  blurb: string
}[] = [
  {
    value: 'guide',
    label: 'Guide me step by step',
    blurb: 'The tutor walks you through each action and moves you on when it sees a task is done.'
  },
  {
    value: 'assist',
    label: 'Assist me when I need help',
    blurb: "You work on your own. The tutor stays quiet until you ask, or until something on screen looks wrong."
  }
]

const STAGES = [
  'Reserving lab environment',
  'Provisioning Windows jump host',
  'Booting ONTAP cluster (cluster3)',
  'Configuring network & SSH access',
  'Connecting Aurilearn Labs Agent'
]

// Sums to ~7s — feels real without being annoying. Replace with actual
// provisioning events when the backend supports them.
const STAGE_DURATIONS_MS = [800, 2500, 1800, 1200, 900]

const fmtMMSS = (seconds: number) => {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0')
  const s = (seconds % 60).toString().padStart(2, '0')
  return `${m}:${s}`
}

// Pulls the module ref from a task ref_code like "M2.E1.T1.S3" → "M2 · E1"
const formatRefCode = (refCode: string | undefined) => {
  if (!refCode) return ''
  const parts = refCode.split('.')
  return parts.slice(0, 2).join(' · ')
}

export const LaunchCard: React.FC<LaunchCardProps> = ({
  exercise,
  mode,
  onLaunch,
  onBack,
  onProvisioned,
  onConnecting,
  launchReady = true,
  teachingMode,
  onTeachingModeChange,
}) => {
  const [stageIdx, setStageIdx] = useState(0)
  const [elapsed, setElapsed] = useState(0)

  // Reset progression each time provisioning kicks off
  useEffect(() => {
    if (mode === 'provisioning') {
      setStageIdx(0)
      setElapsed(0)
    }
  }, [mode])

  // Kick off the LiveKit connection when the last stage begins
  useEffect(() => {
    if (mode === 'provisioning' && stageIdx === STAGES.length - 1) {
      onConnecting?.()
    }
  }, [mode, stageIdx, onConnecting])

  // Drive stage progression
  useEffect(() => {
    if (mode !== 'provisioning') return
    if (stageIdx >= STAGES.length) {
      const t = setTimeout(onProvisioned, 500)
      return () => clearTimeout(t)
    }
    const t = setTimeout(() => setStageIdx((i) => i + 1), STAGE_DURATIONS_MS[stageIdx])
    return () => clearTimeout(t)
  }, [mode, stageIdx, onProvisioned])

  // Drive elapsed-time clock
  useEffect(() => {
    if (mode !== 'provisioning') return
    const i = setInterval(() => setElapsed((s) => s + 1), 1000)
    return () => clearInterval(i)
  }, [mode])

  const tasks = exercise.tasks || []
  const totalSteps = tasks.reduce((acc, t: any) => acc + (t.steps?.length ?? 0), 0)
  const refDisplay = formatRefCode(exercise.ref_code)

  return (
    <div className="w-full h-full overflow-y-auto" style={{ background: '#f5ecd6' }}>
      <div className="min-h-full flex items-center justify-center px-4 sm:px-6 py-4 sm:py-8">
        <div className="w-full max-w-4xl">
        {/* Eyebrow */}
        <div className="text-center mb-1.5">
          <p
            className="text-[11px] font-semibold tracking-[0.18em] uppercase"
            style={{ color: '#b8923a' }}
          >
            {refDisplay ? `${refDisplay} · Hands-on Lab` : 'Hands-on Lab'}
          </p>
        </div>

        {/* Title + description */}
        <h1
          className="text-center text-lg sm:text-2xl font-bold mb-1.5"
          style={{ color: '#1d160a', letterSpacing: '-0.01em' }}
        >
          {exercise.title}
        </h1>
        <p
          className="text-center text-[13px] leading-relaxed max-w-xl mx-auto mb-3 sm:mb-5 hidden sm:block"
          style={{ color: '#4a3f2a' }}
        >
          You'll work through this exercise inside a streamed Windows jump host
          with full agent assistance.
        </p>

        {/* Card */}
        <div
          className="rounded-2xl border p-3 sm:p-5"
          style={{ background: '#faf4e3', borderColor: '#e4d6b0' }}
        >
          {/* Three meta cards */}
          <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-2.5 sm:mb-4">
            <MetaCard label="Estimated time" value="30" suffix="mins" />
            <MetaCard
              label="Tasks"
              value={tasks.length.toString()}
              suffix={totalSteps ? `· ${totalSteps} steps` : undefined}
            />
            <MetaCard label="Environment" value="Windows + ONTAP 9.14" small />
          </div>

          {/*
            Two columns on desktop: the passive provisioning readout on the left,
            the one decision the student actually has to make on the right. When
            they stack on mobile the picker comes first — it's a control, the
            stage list isn't.
          */}
          <div className="grid sm:grid-cols-2 gap-2.5 sm:gap-3">
            {/* Stages list */}
            <div
              className="rounded-xl border p-3 sm:p-4 order-last sm:order-none"
              style={{ background: '#ffffff', borderColor: '#f0e6cd' }}
            >
              <div
                className="text-[11px] font-semibold tracking-[0.14em] uppercase mb-2 sm:mb-3"
                style={{ color: mode === 'provisioning' ? '#b8923a' : '#7a6e54' }}
              >
                {mode === 'provisioning'
                  ? `Setting up your lab — ${fmtMMSS(elapsed)}`
                  : "What we'll spin up for you"}
              </div>

              <div className="space-y-2 sm:space-y-2.5">
                {STAGES.map((label, idx) => {
                  const status =
                    mode === 'intro'
                      ? 'pending'
                      : idx < stageIdx
                        ? 'done'
                        : idx === stageIdx
                          ? 'working'
                          : 'pending'
                  return <StageRow key={label} label={label} status={status} />
                })}
              </div>
            </div>

            {/* How should the tutor behave? */}
            <div
              className="rounded-xl border p-3 sm:p-4 order-first sm:order-none"
              style={{ background: '#fffdf6', borderColor: '#d9c48d' }}
            >
              <p className="text-[13px] font-semibold mb-0.5" style={{ color: '#1d160a' }}>
                How should the tutor work with you?
              </p>
              <p className="text-[11px] mb-2.5" style={{ color: '#7a6e54' }}>
                Pick one — you can switch any time during the lab.
              </p>
              <div className="space-y-2">
                {MODES.map((m) => {
                  const selected = teachingMode === m.value
                  return (
                    <button
                      key={m.value}
                      type="button"
                      onClick={() => onTeachingModeChange(m.value)}
                      disabled={mode === 'provisioning'}
                      aria-pressed={selected}
                      className="w-full text-left rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        background: selected ? '#f3e8c8' : '#ffffff',
                        border: selected ? '2px solid #7a5a1e' : '1.5px solid #e4d6b0',
                        // Absorb the extra border width in the padding so picking
                        // a mode doesn't nudge the column's height.
                        padding: selected ? '9px' : '9.5px'
                      }}
                    >
                      <span className="flex items-center gap-2">
                        <span
                          className="w-[16px] h-[16px] rounded-full grid place-items-center shrink-0"
                          style={{
                            background: selected ? '#7a5a1e' : '#ffffff',
                            border: selected ? 'none' : '1.5px solid #c9b483'
                          }}
                        >
                          {selected && (
                            <Check className="w-[10px] h-[10px] text-white" strokeWidth={4} />
                          )}
                        </span>
                        <span className="text-[13px] font-semibold" style={{ color: '#1d160a' }}>
                          {m.label}
                        </span>
                      </span>
                      <span
                        className="block text-[12px] leading-snug mt-1 pl-[24px]"
                        style={{ color: '#4a3f2a' }}
                      >
                        {m.blurb}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="mt-3 sm:mt-4 flex items-center justify-end gap-3">
            <button
              onClick={onBack}
              disabled={mode === 'provisioning'}
              className="inline-flex items-center gap-1.5 px-4 h-10 text-[13px] font-medium rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ color: '#4a3f2a', background: 'transparent' }}
            >
              <ChevronLeft className="w-4 h-4" />
              Back to exercise
            </button>
            <button
              onClick={() => {
                // Spend this user gesture on a one-shot readText() so the
                // browser's clipboard-read permission prompt fires here,
                // away from the RDP canvas. Once the user resolves it
                // (grant or deny), the permission state is persistent for
                // the origin and the RDP session can sync silently — no
                // floating "Paste" affordance intercepting desktop clicks.
                navigator.clipboard?.readText?.().catch(() => {})
                onLaunch()
              }}
              disabled={mode === 'provisioning' || !launchReady}
              className="inline-flex items-center gap-2 px-5 h-10 text-[13px] font-semibold rounded-md text-white transition-all disabled:cursor-not-allowed"
              style={{
                background: mode === 'provisioning' || !launchReady ? '#a89c7e' : '#7a5a1e',
                boxShadow:
                  mode === 'provisioning' || !launchReady ? 'none' : '0 4px 12px rgba(122,90,30,0.25)'
              }}
            >
              {mode === 'provisioning' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Setting up your environment…
                </>
              ) : !launchReady ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading…
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 fill-current" />
                  Launch Lab Setup
                </>
              )}
            </button>
          </div>
        </div>

          <p
            className="text-center text-[11px] mt-2.5 sm:mt-4 hidden sm:block"
            style={{ color: '#7a6e54' }}
          >
            Your AI instructor will join automatically once the environment is ready.
          </p>
        </div>
      </div>
    </div>
  )
}

const MetaCard: React.FC<{
  label: string
  value: string
  suffix?: string
  small?: boolean
}> = ({ label, value, suffix, small }) => (
  <div
    className="rounded-lg border px-2 sm:px-4 py-2 sm:py-3"
    style={{ background: '#ffffff', borderColor: '#f0e6cd' }}
  >
    <p
      className="text-[9px] sm:text-[10px] font-semibold tracking-[0.12em] uppercase mb-0.5 sm:mb-1 truncate"
      style={{ color: '#7a6e54' }}
    >
      {label}
    </p>
    <div className="flex items-baseline gap-1 flex-wrap">
      <span
        className={small ? 'text-[12px] sm:text-[14px] font-semibold' : 'text-xl sm:text-2xl font-bold'}
        style={{ color: '#1d160a' }}
      >
        {value}
      </span>
      {suffix && (
        <span className="text-[10px]" style={{ color: '#7a6e54' }}>
          {suffix}
        </span>
      )}
    </div>
  </div>
)

const StageRow: React.FC<{ label: string; status: 'pending' | 'working' | 'done' }> = ({
  label,
  status
}) => {
  const indicator =
    status === 'done' ? (
      <div
        className="w-5 h-5 rounded-full grid place-items-center"
        style={{ background: '#7a5a1e' }}
      >
        <Check className="w-3 h-3 text-white" />
      </div>
    ) : status === 'working' ? (
      <div
        className="w-5 h-5 rounded-full grid place-items-center"
        style={{ border: '1.5px solid #b8923a' }}
      >
        <Loader2 className="w-3 h-3 animate-spin" style={{ color: '#b8923a' }} />
      </div>
    ) : (
      <div
        className="w-5 h-5 rounded-full"
        style={{ border: '1.5px solid #e4d6b0', background: '#faf4e3' }}
      />
    )

  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        {indicator}
        <span
          className="text-[14px]"
          style={{
            color: status === 'pending' ? '#7a6e54' : '#1d160a',
            fontWeight: status === 'working' ? 500 : 400
          }}
        >
          {label}
        </span>
      </div>
      <span
        className="text-[12px] font-medium"
        style={{
          color:
            status === 'done' ? '#6b8a3a' : status === 'working' ? '#b8923a' : '#a89c7e'
        }}
      >
        {status === 'done' ? 'Done' : status === 'working' ? 'Working…' : ''}
      </span>
    </div>
  )
}
